// dependencies
import React, { useState, useEffect } from "react";
import { CopyToClipboard } from "react-copy-to-clipboard";
import PropTypes from "prop-types";
import { formatDistanceToNow } from "date-fns";
import Fuse from "fuse.js";
import { Eraser } from "lucide-react";

// components
import TableLite from "Components/TableLite";
import Input from "Components/Inputs/Text";

// actions
import sortFunctions from "./actions";

// selectors

// hooks
import useRequest from "Hooks/useRequest";

// css
import css from "./LambdaFunctions.styl";

// utils
import get from "Utils/get";

/**
 * The purpose of this function is to take a table row (func) and return a
 * formatted link to the branch
 * @param {array} func
 * @returns link element
 */
const selectBranchLink = (func) => {
	let hrefValue = null;

	if (func.runtime === "go1.x") {
		// only for performyardgo funcs (we don't have a branch tag for these)
		hrefValue = "https://github.com/PerformYard/performyardgo";

		return (
			<div>
				<a href={hrefValue} target="_blank" rel="noreferrer">
					performyardgo
				</a>
			</div>
		);
	}

	// for the case where we don't have info on where this code lives
	else if (func.git_branch === "unknown") {
		return "unknown";
	}

	// For all funcs managed by serverless (in LambdaChops) the branch link
	// will be of this form
	hrefValue = `https://github.com/PerformYard/LambdaChops/tree/${func.git_branch}`;

	return (
		<div>
			<a href={hrefValue} target="_blank" rel="noreferrer">
				{get(["git_branch"], func, "unknown")}
			</a>
		</div>
	);
};

/**
 * The purpose of this function is to take a table row (func) and return a
 * formatted link to the commit
 * @param {array} func
 * @returns link element
 */
const selectCommitLink = (func) => {
	let repo = null;

	// for the case where we don't have info on where this code lives
	if (get(["git_hash"], func, "unknown") === "unknown") {
		return "unknown";
	}

	if (func.runtime === "go1.x") {
		repo = "performyardgo";
	} else {
		repo = "LambdaChops";
	}

	// For all funcs managed by serverless (in LambdaChops) the branch link
	// will be of this form
	const hrefValue = `https://github.com/PerformYard/${repo}/commit/${func.git_hash}`;

	return (
		<div>
			<a href={hrefValue} target="_blank" rel="noreferrer">
				{func.git_hash}
			</a>
		</div>
	);
};

const LambdaFunctions = (props) => {
	// fetch values
	const { environment = null } = props;
	const { data, isRequesting, revalidate } = useRequest("Get Lambda Functions");
	const rawLambdaFunctions = data ? data.functions : null;

	// set the initial sort state to be by lastModified date
	const [currentSort, setCurrentSort] = useState({ name: "lastModified", direction: "asc" });
	const [sortedLambdaFunctions, setSortedLambdaFunctions] = useState(rawLambdaFunctions || []);

	// set the copy state
	const [copyState, setCopyState] = useState();

	// environment
	const [environmentName = null, setEnvironment] = useState(null);

	// filters (only "name", currently)
	const [filterState, setFilterState] = useState({
		name: "",
	});

	/**
	 * Handle input changes and update state
	 */
	const handleFilterChange = (name, value) => {
		setFilterState({ ...filterState, [name]: value });
	};

	/**
	 * Sort the filtered functions if there is a filter, else use the raw data.
	 */
	const smartSort = () => {
		// If there is a filter, sort only the filtered results
		if (filterState.name.length > 0) {
			setSortedLambdaFunctions(sortFunctions(sortedLambdaFunctions, currentSort.name, currentSort.direction));
		}
		// Otherwise, sort all results.
		else if (rawLambdaFunctions) {
			setSortedLambdaFunctions(sortFunctions(rawLambdaFunctions, currentSort.name, currentSort.direction));
		}
	};

	useEffect(() => {
		smartSort();
	}, [currentSort, rawLambdaFunctions]);

	useEffect(() => {
		// fetch new set of data when the environment changes
		if (environmentName && !isRequesting) {
			revalidate();
			console.log("revalidating");
		}
	}, [environmentName]);

	useEffect(() => {
		// set environmentName when the environment changes
		if (environment) {
			setEnvironment(environment);
		}
	}, [environment]);

	useEffect(() => {
		// resets the copy state after 1500 MS to allow user to copy again
		setTimeout(() => {
			setCopyState();
		}, 1500);
	}, [copyState]);

	/**
	 * trigger sorting when a user clicks on a column header
	 * @param {string} columnID
	 * @param {string} direction
	 */
	const handleSortColumn = (columnID, direction) => {
		setCurrentSort({ name: columnID, direction });
		smartSort();
	};

	const tableLiteColumns = [
		{
			id: "name",
			label: "Name",
			width: "3fr",
			getter: (row) => (
				<div>
					<a
						href={`https://us-east-1.console.aws.amazon.com/lambda/home?region=us-east-1#/functions/${row.name}?tab=configure`}
						target="_blank"
						rel="noreferrer"
					>
						{row.name}
					</a>
				</div>
			),
		},
		{
			id: "runtime",
			sortName: "runtime",
			label: "Runtime",
			width: "1fr",
			getter: (row) => get(["runtime"], row, "unknown"),
		},
		{
			id: "memorySize",
			sortName: "memorySize",
			isSortable: true,
			label: "Memory (MB)",
			width: "1fr",
			getter: (row) => row.memorySize,
		},
		{
			id: "timeout",
			label: "Timeout (S)",
			width: "1fr",
			getter: (row) => row.timeout,
		},
		{
			id: "git_branch",
			sortName: "git_branch",
			label: "Branch",
			width: "3fr",
			getter: (row) => selectBranchLink(row),
		},
		{
			id: "git_hash",
			sortName: "git_hash",
			label: "Hash",
			width: "1fr",
			getter: (row) => selectCommitLink(row),
		},
		{
			id: "lastModified",
			label: "Last Modified",
			width: "2fr",
			sortName: "lastModified",
			getter: (row) => {
				const date = get(["lastModified"], row, null);
				if (date) {
					try {
						return `${formatDistanceToNow(new Date(date), { addSuffix: true })}`;
					} catch (e) {
						return "unknown";
					}
				}
				return "unknown";
			},
		},
		{
			id: "view_logs",
			isSortable: false,
			label: "View Logs",
			width: "1fr",
			getter: (row) => {
				const logLink = `https://us-east-1.console.aws.amazon.com/cloudwatch/home?region=us-east-1#logsV2:log-groups/log-group/$252Faws$252Flambda$252F${row.name}`;
				return (
					<div style={{ overflow: "hidden" }}>
						<div>
							<a
								style={{ width: "50%", float: "left", height: "24px", verticalAlign: "top" }}
								href={logLink}
								target="_blank"
								rel="noreferrer"
							>
								CloudWatch
							</a>
						</div>
					</div>
				);
			},
		},
		{
			id: "view_logs_cli",
			isSortable: false,
			label: "",
			width: "3fr",
			getter: (row) => {
				const cliCommand = `aws logs tail /aws/lambda/${row.name} --since 24h --format short`;
				return (
					<div style={{ overflow: "hidden" }}>
						<div>
							{copyState && copyState === row.name ? (
								<div className={css.copiedAlert}>Copied!</div>
							) : (
								<CopyToClipboard text={cliCommand} onCopy={() => setCopyState(row.name)}>
									<button className={css.buttonAsALink}>Copy CLI Command</button>
								</CopyToClipboard>
							)}
						</div>
					</div>
				);
			},
		},
	];

	/**
	 * Performs a fuzzy search on the sorted functions data set
	 *
	 * @param {*} searchTerm  - The search term you wish to apply to the data
	 *  set
	 * @returns the results of the search
	 */
	const fuzzySearchFunctions = (searchTerm) => {
		const options = {
			includeScore: true,
			keys: ["name", "arn", "env.lambda_job_id"],
		};
		const fuse = new Fuse(rawLambdaFunctions, options);
		return fuse.search(searchTerm);
	};

	/**
	 * update function table data when filters change with debounce
	 */
	useEffect(() => {
		// If no typing for 6 seconds, update the filters
		const timer = setTimeout(() => {
			if (rawLambdaFunctions && rawLambdaFunctions.length > 0) {
				// Change is to use a filter
				if (filterState.name.trim().length > 0) {
					const fuzzySearchResults = fuzzySearchFunctions(filterState.name);
					const filteredSortedFunctions = fuzzySearchResults.map((row) => row.item);
					setSortedLambdaFunctions(filteredSortedFunctions);
					setCurrentSort({ name: null, direction: null });
				}
				// Change is to remove the filter
				else {
					setSortedLambdaFunctions(rawLambdaFunctions);
					if (!currentSort.name) {
						setCurrentSort({ name: "lastModified", direction: "asc" });
					}
				}
			}
		}, 600);

		// Reset the timer on additional typing
		return () => {
			clearTimeout(timer);
		};
	}, [filterState]);

	/**
	 * Clears all search filters
	 */
	const handleClear = () => {
		setFilterState({ name: "" });
	};

	return (
		<div>
			<div className={css.filters}>
				<div className={css.filtersContainer}>
					<div className={css.filterBox}>
						<label>Function Name</label>
						<div className={css.filterInput}>
							<Input value={filterState.name} onChange={(value) => handleFilterChange("name", value)} />
						</div>
					</div>
					<div
						title="This button will clear the current filters."
						className={css.clearButton}
						onClick={handleClear}
					>
						<div className={css.iconWrapper}>
							<Eraser tabIndex="0" size={16} />
						</div>
					</div>
				</div>
			</div>
			<div className={css.lambdaTable} test_id="lambdatable">
				<TableLite
					columns={tableLiteColumns}
					rows={sortedLambdaFunctions}
					isLoading={isRequesting}
					activeSort={currentSort.name}
					activeSortDirection={currentSort.direction}
					onColumnClick={handleSortColumn}
					headCellClass={css.lambdaFunctionTableHeaderCell}
				/>
			</div>
		</div>
	);
};

LambdaFunctions.propTypes = { environment: PropTypes.string.isRequired };

export default LambdaFunctions;
